/** A station's health in words and the approved status colours (intake v2
 *  phase 5; DESIGN_GUIDELINE §2.6). The backend's health monitor opens and
 *  closes the alerts; this only says what they mean. */
import type { Station, StationAlert } from '../api/types'
import { formatDateTime, relativeAge } from './time'

export type HealthStatus = 'alarm' | 'warning' | 'normal' | 'stale'

/** The checks run every minute; three without one and the page says so. */
export const CHECKS_STALE_MS = 3 * 60_000
const RANK: Record<HealthStatus, number> = { alarm: 0, warning: 1, stale: 2, normal: 3 }

/** Silent and intake stuck are alarms (nothing arriving; data acknowledged
 *  and not stored); source failing is a warning (the vendor keeps the data);
 *  quiet channels are information and change no status. */
export function severity(alert: StationAlert): HealthStatus | 'info' {
  switch (alert.kind) {
    case 'silent': case 'intake_stuck': return 'alarm'
    case 'source_failing': return 'warning'
    default: return 'info'
  }
}

export function checksStale(checkedAt: string | null | undefined, now = new Date()): boolean {
  if (!checkedAt) return true
  return now.getTime() - new Date(checkedAt).getTime() > CHECKS_STALE_MS
}

/** How long, for a label: "45 min", "4 h", "3 days". */
export function duration(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000))
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} h`
  return `${Math.floor(hours / 24)} days`
}

function plural(n: number, word: string, many = `${word}s`): string {
  return `${n} ${n === 1 ? word : many}`
}

function label(alert: StationAlert, now: Date): string {
  switch (alert.kind) {
    case 'silent': {
      const last = alert.detail.last_reading_at
      return last ? `no readings for ${duration(now.getTime() - new Date(last).getTime())}`
        : 'no readings'
    }
    case 'intake_stuck': return 'pushes not stored'
    case 'source_failing': return alert.detail.gateway ? 'gateway not answering' : 'collection failing'
    case 'channels_quiet': return `${plural(alert.detail.count, 'channel')} quiet`
    case 'still_on_mds': return 'pushing, still on MDS'
  }
}

export interface Health { status: HealthStatus; label: string }

export function stationHealth(station: Station, now = new Date()): Health {
  if (!station.active) return { status: 'stale', label: 'decommissioned' }
  if (!station.schedule_enabled) return { status: 'stale', label: 'collection off' }
  if (checksStale(station.health_checked_at, now)) return { status: 'stale', label: 'monitoring delayed' }
  const ranked = (station.alerts ?? [])
    .filter((a) => severity(a) !== 'info')
    .sort((a, b) => RANK[severity(a) as HealthStatus] - RANK[severity(b) as HealthStatus])
  const top = ranked[0]
  if (top) return { status: severity(top) as HealthStatus, label: label(top, now) }
  if (!station.last_measurement_at) return { status: 'stale', label: 'no data' }
  return { status: 'normal', label: 'normal' }
}

export function needsAttention(station: Station, now = new Date()): boolean {
  const { status } = stationHealth(station, now)
  return status === 'alarm' || status === 'warning'
}

export interface AlertText { headline: string; detail: string }

/** An alert as sentences. What recovers and what does not is part of a
 *  gateway's message: a reader deciding whether to phone the site needs it.
 *  Viewers read these too, so none names the admin-only Intake page. */
export function alertText(name: string, alert: StationAlert, now = new Date()): AlertText {
  switch (alert.kind) {
    case 'silent': {
      const { last_reading_at: last, expected_s, after_s } = alert.detail
      return {
        headline: last
          ? `${name} has sent no readings since ${formatDateTime(last)} (${relativeAge(last, now)}).`
          : `${name} has sent no readings in the last 30 days.`,
        detail: `Readings are expected about every ${duration(expected_s * 1000)}; `
          + `this is raised after ${duration(after_s * 1000)} without one.`,
      }
    }
    case 'source_failing': {
      const { gateway, tasks } = alert.detail
      if (gateway) {
        const since = gateway.last_ok_at ?? gateway.failing_since
        const recovery = 'Readings the gateway records meanwhile are collected once it '
          + 'answers again; link-quality figures for the gap are not.'
        return {
          headline: `${name}'s gateway has not answered since ${formatDateTime(since)} `
            + `(${relativeAge(since, now)}).`,
          detail: (gateway.last_error ? `Last error: ${gateway.last_error}. ` : '') + recovery,
        }
      }
      const failing = tasks.map((t) => `${t.task}${t.last_error ? ` (${t.last_error})` : ''}`)
      const since = tasks.map((t) => t.failing_since).sort()[0] ?? alert.opened_at
      return {
        headline: `${name}: collection failing since ${formatDateTime(since)} (${relativeAge(since, now)}).`,
        detail: `Failing: ${failing.join('; ')}. The vendor keeps the readings; they are `
          + 'collected once it recovers.',
      }
    }
    case 'intake_stuck': {
      const d = alert.detail
      const parts: string[] = []
      if (d.failed) parts.push(`${plural(d.failed, 'push', 'pushes')} could not be read in the last 7 days; `
        + 'an administrator can have them read again once the cause is fixed.')
      if (d.oldest_pending_at) parts.push('The oldest push waiting arrived '
        + `${formatDateTime(d.oldest_pending_at)} (${relativeAge(d.oldest_pending_at, now)}).`)
      if (d.refusals) parts.push(`${plural(d.refusals, 'push', 'pushes')} refused in the last hour: `
        + `the logger sent station id ${d.last_refusal_claimed ?? '?'} under this station's login.`)
      return { headline: `${name}: pushes are arriving but not all are stored.`, detail: parts.join(' ') }
    }
    case 'channels_quiet': {
      const { count, channels } = alert.detail
      const named = channels.slice(0, 5).map((c) =>
        `${c.instrument ? `${c.instrument} · ` : ''}${c.name} (last ${formatDateTime(c.last_at)})`)
      return {
        headline: `${plural(count, 'channel')} at ${name} ${count === 1 ? 'has' : 'have'} `
          + 'sent nothing for over a day while the rest report.',
        detail: `${named.join(', ')}${count > named.length ? ', …' : ''}. This names channels; `
          + "it does not change the station's status.",
      }
    }
    case 'still_on_mds': {
      const last = alert.detail.last_push_at
      return {
        headline: `${name}'s logger is pushing to the platform, but MDS still collects it.`,
        detail: `Last push ${formatDateTime(last)} (${relativeAge(last, now)}). The pushes are `
          + 'kept and not yet written; an administrator switches the station over to them. '
          + 'Its readings keep arriving from MDS until then.',
      }
    }
  }
}
